import { Button, Flex, IconExport } from '@gmg/gmg-react-components';
import { FunctionComponent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AppContext from 'src/AppContext';
import { getGoToHelpDeskEvent, getOpenOrDownloadAppEvent } from 'src/tracking';
import { DesktopApplicationViewModel } from 'src/viewModels/Products';
import styled from 'styled-components';

interface ProductItemProps {
    product: DesktopApplicationViewModel;
}

const ProductItemDesktop: FunctionComponent<ProductItemProps> = ({ product: { productName, logo, helpDeskUrl, productDefinitions } }) => {
    const { t } = useTranslation();
    const { trackEvent, language } = useContext(AppContext);

    // add isWindows to each definition
    const definitionsWithOS = productDefinitions.map(def => ({
        ...def,
        isWindows: def.downloadUrl.toLowerCase().endsWith('.exe'),
    }))
        .slice(0, 2) //* SLice is needed because Measure has more then 2 def, it has to be fixed in CLP by Jo. L.
        .sort((a, _b) => a.isWindows ? 1 : -1);

    return (
        <Item key={productName} showTwoButtons={definitionsWithOS.length > 1}>
            <span aria-label={`${productName} Logo`}>
                {logo}
            </span>
            <span className="px12_600_contrast" aria-label={productName}>
                {productName}
            </span>
            <>
                {definitionsWithOS.map((def) => (
                    <NoneDecoratedLink
                        to={def.downloadUrl}
                        key={def.downloadUrl}
                        aria-label={`Download ${productName} for ${def.isWindows ? 'Windows' : 'Mac'}`}
                        onClick={() => {
                            const osSuffix = def.isWindows ? '_Win' : '_Mac';
                            trackEvent(getOpenOrDownloadAppEvent(`${productName}${osSuffix}`));
                        }}
                    >
                        <StyledButton
                            size="normal"
                            variant="secondary"
                        >
                            {def.isWindows
                                ? t('UserProfile.download_win_button', 'Download for Windows')
                                : t('UserProfile.download_mac_button', 'Download for Mac')
                            }
                        </StyledButton>
                    </NoneDecoratedLink>
                ))}
                <Flex width='100%' justifyContent='center' className='px12_600_normal'>
                    {definitionsWithOS[0].version ?? '—'}
                </Flex>
                <Flex justifyContent='center' alignItems='center' width='100%'>
                    <NoneDecoratedLink
                        target="_blank"
                        rel="noopener noreferrer"
                        to={helpDeskUrl[language === 'de' ? 'de' : 'en']}
                        aria-label={`Help center for ${productName}`}
                        onClick={() => {
                            trackEvent(getGoToHelpDeskEvent(productName));
                        }}
                    >
                        <IconExport height={16} width={16} />
                    </NoneDecoratedLink>
                </Flex>
            </>
        </Item>);
};

export const Item = styled.div<{ showTwoButtons?: boolean }>`    
    display: grid;
    grid-template-columns: ${props => props.showTwoButtons ? '50px auto 170px 170px 82px 68px 48px' : '50px auto 170px 82px 68px 48px'};
    align-items: center;
    width: calc(100% - 12px);
    justify-items: flex-start;
    padding: 4px 0;
    border-top: 1px solid ${props => props.theme.colors.backgroundPaper};
    border-bottom: 1px solid ${props => props.theme.colors.backgroundPaper};
    column-gap: 15px;
`;

export const NoneDecoratedLink = styled(Link)`
    text-decoration: none;
    width: 100%;
`;

export const StyledButton = styled(Button)`
    width: 100%;
`;

export default ProductItemDesktop;